import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Calendar, Activity, Phone, Mail, ChevronDown } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { parseCSV } from '../utils/csvParser';

function Home() {
  const [selectedPeriod, setSelectedPeriod] = useState('90');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [journalData, setJournalData] = useState([]);
  const [stats, setStats] = useState({
    totalPnL: 0,
    totalDays: 0,
    profitDays: 0,
    lossDays: 0,
    winRate: 0,
    avgProfit: 0,
    avgLoss: 0,
    maxProfit: 0,
    maxLoss: 0,
    currentStreak: 0,
    bestStreak: 0
  });
  const [dailyPnL, setDailyPnL] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchJournalData();
  }, []);

  const fetchJournalData = async () => {
    try {
      const response = await fetch('/trading-journal.csv');
      const csvText = await response.text();
      const parsedData = parseCSV(csvText);
      setJournalData(parsedData);
      calculateStats(parsedData, selectedPeriod);
    } catch (err) {
      console.error('Error loading CSV:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data, period) => {
    if (data.length === 0) return;

    let filteredData;
    
    if (period === 'custom' && customDateRange.start && customDateRange.end) {
      // Filter by custom date range
      const startDate = new Date(customDateRange.start);
      const endDate = new Date(customDateRange.end);
      filteredData = data.filter(entry => {
        const entryDate = new Date(entry.date);
        return entryDate >= startDate && entryDate <= endDate;
      });
    } else if (period === 'all') {
      filteredData = data;
    } else {
      // Filter data based on selected period
      const today = new Date();
      const periodDays = parseInt(period);
      const cutoffDate = new Date(today.getTime() - (periodDays * 24 * 60 * 60 * 1000));
      filteredData = data.filter(entry => new Date(entry.date) >= cutoffDate);
    }

    // Sort by date
    const sortedData = [...filteredData].sort((a, b) => new Date(a.date) - new Date(b.date));

    // Calculate statistics
    let totalPnL = 0;
    let profitDays = 0;
    let lossDays = 0;
    let profits = [];
    let losses = [];
    let currentStreak = 0;
    let bestStreak = 0;
    let tempStreak = 0;

    sortedData.forEach(entry => {
      totalPnL += entry.pnl;
      
      if (entry.pnl > 0) {
        profitDays++;
        profits.push(entry.pnl);
        tempStreak++;
        bestStreak = Math.max(bestStreak, tempStreak);
        currentStreak = tempStreak;
      } else if (entry.pnl < 0) {
        lossDays++;
        losses.push(Math.abs(entry.pnl));
        tempStreak = 0;
      }
    });

    const totalDays = sortedData.length;
    const winRate = totalDays > 0 ? ((profitDays / totalDays) * 100).toFixed(1) : 0;
    const avgProfit = profits.length > 0 ? profits.reduce((a, b) => a + b, 0) / profits.length : 0;
    const avgLoss = losses.length > 0 ? losses.reduce((a, b) => a + b, 0) / losses.length : 0;
    const maxProfit = profits.length > 0 ? Math.max(...profits) : 0;
    const maxLoss = losses.length > 0 ? Math.max(...losses) : 0;

    // Prepare daily P&L data for chart
    const dailyData = sortedData.map(entry => ({
      date: new Date(entry.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
      pnl: entry.pnl,
      cumulative: 0
    }));

    // Calculate cumulative P&L
    let cumulative = 0;
    dailyData.forEach(day => {
      cumulative += day.pnl;
      day.cumulative = cumulative;
    });

    setDailyPnL(dailyData);
    setStats({
      totalPnL,
      totalDays,
      profitDays,
      lossDays,
      winRate,
      avgProfit: Math.round(avgProfit),
      avgLoss: Math.round(avgLoss),
      maxProfit,
      maxLoss,
      currentStreak,
      bestStreak
    });
  };

  useEffect(() => {
    if (journalData.length > 0) {
      calculateStats(journalData, selectedPeriod);
    }
  }, [selectedPeriod, journalData, customDateRange]);

  const formatCurrency = (value) => {
    const absValue = Math.abs(value);
    if (absValue >= 100000) {
      return `₹${(value / 100000).toFixed(2)}L`;
    }
    return `₹${value.toLocaleString('en-IN')}`;
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload[0]) {
      return (
        <div style={{
          background: 'white',
          border: '1px solid #e0e0e0',
          borderRadius: '5px',
          padding: '10px',
          boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
        }}>
          <p style={{ margin: 0, fontSize: '0.9rem', color: '#666' }}>{payload[0].payload.date}</p>
          <p style={{ 
            margin: '5px 0 0 0', 
            fontWeight: 'bold',
            color: payload[0].value >= 0 ? '#10b981' : '#ef4444'
          }}>
            P&L: {formatCurrency(payload[0].value)}
          </p>
          {payload[1] && (
            <p style={{ margin: '5px 0 0 0', fontSize: '0.9rem', color: '#666' }}>
              Total: {formatCurrency(payload[1].value)}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="container">
      <div style={{ marginBottom: '30px', textAlign: 'center' }}>
        <h1 style={{ fontSize: '2.2rem', fontWeight: '700', color: '#333', marginBottom: '10px' }}>
          Trade Like an Institute
        </h1>
        <p style={{ 
          color: '#667eea', 
          fontSize: '1.1rem', 
          fontWeight: '500',
          marginBottom: '5px',
          fontStyle: 'italic'
        }}>
          "You can lose battles, but never lose the war"
        </p>
        <p style={{ color: '#666', fontSize: '0.95rem' }}>
          Systematic algorithmic trading with institutional discipline
        </p>
      </div>

      <div style={{ 
        background: 'white',
        borderRadius: '8px',
        padding: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '30px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
          <h2 style={{ fontSize: '1.2rem', fontWeight: '600', color: '#333', margin: 0 }}>
            P&L Summary
          </h2>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            {['7', '30', '90', '365', 'all'].map(period => (
              <button
                key={period}
                onClick={() => {
                  setSelectedPeriod(period);
                  setShowCustomDate(false);
                }}
                style={{
                  padding: '6px 16px',
                  border: '1px solid',
                  borderColor: selectedPeriod === period && !showCustomDate ? '#667eea' : '#e0e0e0',
                  background: selectedPeriod === period && !showCustomDate ? '#667eea' : 'white',
                  color: selectedPeriod === period && !showCustomDate ? 'white' : '#666',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
              >
                {period === 'all' ? 'All' : `${period}D`}
              </button>
            ))}
            <button
              onClick={() => {
                setShowCustomDate(!showCustomDate);
                setSelectedPeriod('custom');
              }}
              style={{
                padding: '6px 16px',
                border: '1px solid',
                borderColor: showCustomDate ? '#667eea' : '#e0e0e0',
                background: showCustomDate ? '#667eea' : 'white',
                color: showCustomDate ? 'white' : '#666',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}
            >
              <Calendar size={16} />
              Custom
            </button>
          </div>
        </div>
        
        {showCustomDate && (
          <div style={{ 
            marginBottom: '20px', 
            padding: '15px',
            background: '#f8f9fa',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ fontSize: '0.9rem', color: '#666', fontWeight: '500' }}>From:</label>
              <input
                type="date"
                value={customDateRange.start}
                onChange={(e) => setCustomDateRange({ ...customDateRange, start: e.target.value })}
                style={{
                  padding: '6px 10px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  fontSize: '0.9rem'
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ fontSize: '0.9rem', color: '#666', fontWeight: '500' }}>To:</label>
              <input
                type="date"
                value={customDateRange.end}
                onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
                style={{
                  padding: '6px 10px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  fontSize: '0.9rem'
                }}
              />
            </div>
            <button
              onClick={() => {
                if (customDateRange.start && customDateRange.end) {
                  calculateStats(journalData, 'custom');
                }
              }}
              style={{
                padding: '6px 20px',
                background: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}
            >
              Apply
            </button>
          </div>
        )}

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '20px',
          marginBottom: '30px'
        }}>
          <div>
            <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '5px' }}>Total P&L</div>
            <div style={{ 
              fontSize: '1.5rem', 
              fontWeight: 'bold',
              color: stats.totalPnL >= 0 ? '#10b981' : '#ef4444',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}>
              {stats.totalPnL >= 0 ? <TrendingUp size={20} /> : <TrendingDown size={20} />}
              {formatCurrency(stats.totalPnL)}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '5px' }}>Win Rate</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#333' }}>
              {stats.winRate}%
            </div>
          </div>

          <div>
            <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '5px' }}>Profit Days</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#10b981' }}>
              {stats.profitDays}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '5px' }}>Loss Days</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ef4444' }}>
              {stats.lossDays}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '5px' }}>Avg Profit</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#10b981' }}>
              {formatCurrency(stats.avgProfit)}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '5px' }}>Avg Loss</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ef4444' }}>
              {formatCurrency(stats.avgLoss)}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#333', marginBottom: '15px' }}>
            Daily P&L
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dailyPnL}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: '0.8rem', fill: '#666' }}
                interval={'preserveStartEnd'}
              />
              <YAxis 
                tick={{ fontSize: '0.8rem', fill: '#666' }}
                tickFormatter={(value) => `₹${(value/1000).toFixed(0)}k`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="pnl" radius={[4, 4, 0, 0]}>
                {dailyPnL.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.pnl >= 0 ? '#10b981' : '#ef4444'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div>
          <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#333', marginBottom: '15px' }}>
            Cumulative P&L
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={dailyPnL}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: '0.8rem', fill: '#666' }}
                interval={'preserveStartEnd'}
              />
              <YAxis 
                tick={{ fontSize: '0.8rem', fill: '#666' }}
                tickFormatter={(value) => `₹${(value/1000).toFixed(0)}k`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="monotone" 
                dataKey="cumulative" 
                stroke="#667eea" 
                strokeWidth={2}
                dot={{ fill: '#667eea', r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '15px',
        marginBottom: '30px'
      }}>
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '5px' }}>Max Profit Day</div>
          <div style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#10b981' }}>
            {formatCurrency(stats.maxProfit)}
          </div>
        </div>

        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '5px' }}>Max Loss Day</div>
          <div style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#ef4444' }}>
            {formatCurrency(stats.maxLoss)}
          </div>
        </div>

        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '5px' }}>Current Streak</div>
          <div style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#667eea' }}>
            {stats.currentStreak} days
          </div>
        </div>

        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '5px' }}>Best Streak</div>
          <div style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#667eea' }}>
            {stats.bestStreak} days
          </div>
        </div>
      </div>

      <div style={{
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
        borderRadius: '8px',
        padding: '40px',
        textAlign: 'center',
        marginTop: '40px',
        marginBottom: '40px',
        border: '2px solid #667eea'
      }}>
        <h2 style={{ 
          fontSize: '1.5rem', 
          fontWeight: '600', 
          color: '#667eea', 
          marginBottom: '15px',
          fontStyle: 'italic' 
        }}>
          "The goal isn't to be right every time, it's to be profitable over time"
        </h2>
        <p style={{ color: '#666', fontSize: '1rem', lineHeight: '1.6' }}>
          Professional traders know that you can be wrong 40% of the time and still make money. 
          It's not about avoiding losses - it's about managing them. Cut your losses short, 
          let your winners run, and the math takes care of itself.
        </p>
      </div>

      <div style={{
        background: '#f8f9fa',
        borderRadius: '8px',
        padding: '30px',
        textAlign: 'center',
        marginTop: '50px'
      }}>
        <h3 style={{ fontSize: '1.2rem', fontWeight: '600', color: '#333', marginBottom: '10px' }}>
          Need Help with Algorithmic Trading Setup?
        </h3>
        <p style={{ color: '#666', marginBottom: '20px', fontSize: '0.95rem' }}>
          For assistance with algorithm development, backtesting, or deployment
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '30px', flexWrap: 'wrap' }}>
          <a href="mailto:algo.support@keketech.com" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            color: '#667eea',
            textDecoration: 'none',
            fontSize: '1rem'
          }}>
            <Mail size={20} />
            keketechnologyandtradellp@gmail.com
          </a>
          <a href="tel:+919876543210" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            color: '#667eea',
            textDecoration: 'none',
            fontSize: '1rem'
          }}>
            <Phone size={20} />
            +91 77088 02147
          </a>
        </div>
      </div>
    </div>
  );
}

export default Home;